import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/store/auth'
import { LandingPage } from '@/pages/LandingPage'
import { LoginPage } from '@/pages/LoginPage'
import { SignupPage } from '@/pages/SignupPage'
import { EmailVerificationPage } from '@/pages/EmailVerificationPage'
import { ForgotPasswordPage } from '@/pages/ForgotPasswordPage'
import { ChatPage } from '@/pages/ChatPage'
import { ChatHistoryPage } from '@/pages/ChatHistoryPage'
import { AuthTestPage } from '@/pages/AuthTestPage'
import { BetaSignupPage } from '@/pages/BetaSignupPage'
import { AboutPage } from '@/pages/AboutPage'
import { PrivacyPolicyPage } from '@/pages/PrivacyPolicyPage'
import { TermsOfServicePage } from '@/pages/TermsOfServicePage'
import { DeveloperPage } from '@/pages/DeveloperPage'
import { useEffect, useState, useMemo } from 'react'
import { ReactLenis } from 'lenis/react'
import { Toaster } from 'react-hot-toast'
import { motion, AnimatePresence } from 'framer-motion'
import toast from 'react-hot-toast'
import { Analytics } from '@vercel/analytics/react'
import { SpeedInsights } from '@vercel/speed-insights/react'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { useNavigate } from 'react-router-dom'

function OAuthLoadingScreen() {
  const [timeoutWarning, setTimeoutWarning] = useState(false)
  const { clearAuth } = useAuthStore()

  useEffect(() => {
    // Show warning after 10 seconds
    const warningTimer = setTimeout(() => {
      setTimeoutWarning(true)
    }, 10000)

    // Force redirect after 60 seconds if still loading (increased from 30s)
    const timeoutTimer = setTimeout(() => {
      toast.error('Authentication is taking too long. Please try again.')
      clearAuth()
      window.location.href = '/login'
    }, 60000)

    return () => {
      clearTimeout(warningTimer)
      clearTimeout(timeoutTimer)
    }
  }, [clearAuth])

  return (
    <motion.div
      className="min-h-screen bg-gradient-to-br from-black via-orange-900/20 to-black flex items-center justify-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="text-center max-w-md mx-auto px-6">
        {/* Enhanced Loading Animation */}
        <div className="relative mb-8">
          <div className="w-20 h-20 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <div className="absolute inset-0 w-20 h-20 border-4 border-orange-300 border-opacity-30 rounded-full mx-auto animate-pulse"></div>
        </div>

        {/* Google Logo Animation */}
        <div className="mb-6">
          <div className="w-12 h-12 mx-auto bg-white rounded-full flex items-center justify-center shadow-lg">
            <svg className="w-6 h-6" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
          </div>
        </div>

        <motion.h2
          className="text-2xl font-bold text-white mb-3"
          initial={{ y: 10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          Signing you in...
        </motion.h2>

        <motion.p
          className="text-gray-300 mb-6"
          initial={{ y: 10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          Processing your Google authentication
        </motion.p>

        {/* Progress steps */}
        <div className="flex justify-center space-x-2 mb-6">
          <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
          <div className="w-2 h-2 bg-orange-400 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
          <div className="w-2 h-2 bg-orange-300 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
        </div>

        {timeoutWarning && (
          <motion.div
            className="bg-yellow-500 bg-opacity-20 border border-yellow-500 rounded-lg p-4 text-yellow-200 mb-4"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <p className="text-sm">
              This is taking longer than usual. If the process doesn't complete soon,
              you'll be redirected to try again.
            </p>
          </motion.div>
        )}

        <button
          onClick={() => {
            clearAuth()
            window.location.href = '/login'
          }}
          className="mt-4 px-4 py-2 text-gray-400 hover:text-white hover:bg-gray-800 
                     text-sm rounded-lg transition-all duration-200 border border-gray-600 
                     hover:border-gray-500"
        >
          Cancel and return to login
        </button>
      </div>
    </motion.div>
  )
}

/**
 * Declarative component to handle subdomain-based redirection from the root path.
 */
function RootSubdomainRedirector() {
  const { isAuthenticated, isInitializing } = useAuthStore()
  const hostname = window.location.hostname
  const parts = hostname.split('.')

  if (parts.length >= 3) {
    const subdomain = parts[0]

    // Skip www subdomain
    if (subdomain === 'www') return <LandingPage />

    if (subdomain === 'chat') {
      if (isInitializing) {
        return (
          <div className="min-h-screen bg-black flex items-center justify-center">
            <div className="text-white text-center">
              <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            </div>
          </div>
        )
      }
      return isAuthenticated ? <ChatPage /> : <Navigate to="/login" replace />
    }
    if (subdomain === 'beta') return <BetaSignupPage />
    if (subdomain === 'about') return <AboutPage />
    if (subdomain === 'dev') return <DeveloperPage />
  }

  return <LandingPage />
}

function AppContent() {
  const { isAuthenticated, isInitializing, hasOAuthToken, checkAuth } = useAuthStore()
  const location = useLocation()
  const navigate = useNavigate()
  // No longer blocking render on this state
  const [authInitialized, setAuthInitialized] = useState(false)

  // First, initialize auth - runs ONCE on mount only
  useEffect(() => {
    const init = async () => {
      console.log('🔐 [Auth] Initializing...')
      await checkAuth()
      setAuthInitialized(true)
      console.log('✅ [Auth] Initialized. Authenticated:', useAuthStore.getState().isAuthenticated)
    }
    init()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Empty dependency - only run once on mount

  // Show loading screen during OAuth processing ONLY
  if (isInitializing && hasOAuthToken()) {
    return <OAuthLoadingScreen />
  }

  // Disable smooth scrolling for chat pages to prevent conflicts
  const isChatPage = location.pathname.includes('/chat')

  // COMPLETELY REBUILT ULTRA-SMOOTH PAGE TRANSITIONS
  const pageVariants = {
    initial: {
      opacity: 0,
      y: 100,
      scale: 0.85,
      rotateX: 15,
      rotateY: 10,
      filter: "blur(40px) brightness(0.3)",
      transform: "perspective(1000px) translateZ(-100px)"
    },
    in: {
      opacity: 1,
      y: 0,
      scale: 1,
      rotateX: 0,
      rotateY: 0,
      filter: "blur(0px) brightness(1)",
      transform: "perspective(1000px) translateZ(0px)"
    },
    out: {
      opacity: 0,
      y: -100,
      scale: 1.15,
      rotateX: -15,
      rotateY: -10,
      filter: "blur(40px) brightness(0.3)",
      transform: "perspective(1000px) translateZ(-100px)"
    }
  }

  const pageTransition = {
    type: "spring",
    damping: 15,
    stiffness: 60,
    mass: 1.5,
    duration: 1.5
  }

  // Stunning gradient backgrounds based on route
  const getStunningGradientForRoute = (pathname: string) => {
    if (pathname === '/') {
      return 'bg-gradient-to-br from-black via-orange-900/20 to-black'
    } else if (pathname.includes('/chat')) {
      return 'bg-black'
    } else if (pathname.includes('/login') || pathname.includes('/signup') || pathname.includes('/verify') || pathname.includes('/forgot')) {
      return 'bg-gradient-to-br from-black via-orange-800/10 to-black'
    }
    return 'bg-gradient-to-br from-black via-orange-900/15 to-black'
  }

  return (
    <div className={`min-h-screen relative ${getStunningGradientForRoute(location.pathname)}`}>
      <Routes>
        <Route path="/" element={<RootSubdomainRedirector />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/verify-email" element={<EmailVerificationPage />} />
        <Route path="/chat" element={<ChatPage />} />
        <Route path="/chat/:chatId" element={<ChatPage />} />
        <Route path="/chats" element={<ChatHistoryPage />} />
        <Route path="/beta" element={<BetaSignupPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsOfServicePage />} />
        <Route path="/legal" element={<TermsOfServicePage />} />
        <Route path="/developers" element={<DeveloperPage />} />
        <Route path="/auth-test" element={<AuthTestPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  )
}

export default function App() {
  return (
    <ErrorBoundary>
      <ReactLenis root options={{ lerp: 0.08, duration: 1.4, smoothWheel: true }}>
      <Router>
        <AppContent />
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: 'rgba(0, 0, 0, 0.9)',
              color: '#ffffff',
              border: '1px solid rgba(255, 107, 53, 0.3)',
              borderRadius: '16px',
              backdropFilter: 'blur(16px)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), 0 0 20px rgba(255, 107, 53, 0.2)',
            },
            success: {
              style: {
                background: 'rgba(34, 197, 94, 0.15)',
                border: '1px solid rgba(34, 197, 94, 0.4)',
                color: '#ffffff',
              },
            },
            error: {
              style: {
                background: 'rgba(239, 68, 68, 0.15)',
                border: '1px solid rgba(239, 68, 68, 0.4)',
                color: '#ffffff',
              },
            },
          }}
        />
        <Analytics />
        <SpeedInsights />
      </Router>
      </ReactLenis>
    </ErrorBoundary>
  )
}
